import React, { Suspense } from "react";
import { QueryErrorResetBoundary, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { Box, Container } from "@mui/material";
import Navbar from "./Navbar";
import Footer from "./Footer";
import DataDevice from "./DataDevice";
import ErrorFallback from "./ErrorFallback";
import LoadingComponent from "./LoadingComponent";
import QrCodeNotFoundError from "./QrCodeNotFoundError";
import { useParams, useNavigate } from "react-router-dom";
import { fetchDataDevice } from "../services/api";

const HomePage = () => {
  const { encode } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["dataDevice", encode],
    queryFn: () => fetchDataDevice(encode),
    retry: false,
    enabled: !!encode
  });

  // Check if error is QR code not found
  const isQrCodeNotFound = error?.code === "QR_CODE_NOT_FOUND" || error?.code === "NO_SERIAL_NUMBER";

  const handleTryAgain = () => {
    // Clear the error and allow user to scan again
    queryClient.resetQueries(["dataDevice", encode]);
    // Stay on the same URL, just reset the error state
    // User can scan a new QR code which will update the URL parameter
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingComponent/>;
    }

    // Show QR code not found error
    if (isQrCodeNotFound) {
      return (
        <QrCodeNotFoundError 
          scannedCode={error?.scannedCode || encode} 
          onTryAgain={handleTryAgain}
        />
      );
    }

    if (error) {
      return <ErrorFallback error={error} resetErrorBoundary={handleTryAgain} />;
    }

    return (
      <DataDevice
        isLoading={isLoading}
        LoadingComponent={LoadingComponent}
        encode={encode}
        data={data}
      />
    );
  };

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={reset}
        >
          <Suspense fallback={<LoadingComponent />}>
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
              <Navbar />
              <Container 
                component="main" 
                maxWidth="sm" 
                sx={{ 
                  mb: 2, 
                  flex: 1, 
                  display: "flex", 
                  flexDirection: "column", 
                  py: 2, 
                  px: { xs: 0 } 
                }}
              >
                {renderContent()}
              </Container>
              <Footer />
            </Box>
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
};

export default HomePage;